import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ApiService } from "../../services/api";
import "./image_creation.scss";

const API_ORIGIN = "https://api.zulluai.com";

const getAssetUrl = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `${API_ORIGIN}${url}`;
};

const getUserIdentifier = () => {
  const rawId =
    localStorage.getItem("phoneNumber") || localStorage.getItem("userEmail");

  if (!rawId) return "";
  return rawId.includes("@") || rawId.startsWith("+") ? rawId : `+${rawId}`;
};

const ImageCreation = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      const identifier = getUserIdentifier();
      if (!identifier) {
        setLoading(false);
        return;
      }

      try {
        const response = await ApiService.getUserImageHistory(identifier);
        if (Array.isArray(response)) {
          setImages(response);
        } else if (Array.isArray(response?.data)) {
          setImages(response.data);
        }
      } catch (error) {
        console.error("Failed to load image history", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  const handleDownload = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "zullu-image.png");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Image download failed:", error);
      window.open(imageUrl, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="image-gallery-state">
        <div className="gallery-spinner"></div>
        <p>Arranging your image gallery...</p>
      </div>
    );
  }

  if (!images.length) {
    return (
      <div className="image-empty-state">
        <i className="fi fi-rr-picture"></i>
        <h3>No images yet</h3>
        <p>Your generated images will appear here once you create them.</p>
      </div>
    );
  }

  return (
    <>
      <div className="image-gallery-grid">
        {images.map((image, index) => {
          const imageUrl = getAssetUrl(image.imageUrl);
          return (
            <motion.article
              key={image._id}
              className="image-tile"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.035, 0.35) }}
            >
              <button
                className="image-preview-button"
                onClick={() => setSelectedImage(image)}
                aria-label="Open generated image"
              >
                <img src={imageUrl} alt={image.prompt || "Generated image"} />
              </button>
              <div className="image-tile-info">
                <p>{image.prompt}</p>
                <div className="image-tile-footer">
                  <span>{new Date(image.createdAt).toLocaleDateString()}</span>
                  <button
                    type="button"
                    onClick={() =>
                      handleDownload(imageUrl, `zullu_${image._id}.png`)
                    }
                    aria-label="Download image"
                  >
                    <i className="fi fi-rr-download"></i>
                  </button>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>

      {selectedImage && (
        <div
          className="image-lightbox"
          onClick={() => setSelectedImage(null)}
          role="presentation"
        >
          <motion.div
            className="image-lightbox-panel"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close preview"
            >
              <i className="fi fi-rr-cross-small"></i>
            </button>
            <img
              src={getAssetUrl(selectedImage.imageUrl)}
              alt={selectedImage.prompt || "Generated image preview"}
            />
            <div className="lightbox-info">
              <p>{selectedImage.prompt}</p>
              <button
                type="button"
                onClick={() =>
                  handleDownload(
                    getAssetUrl(selectedImage.imageUrl),
                    `zullu_${selectedImage._id}.png`
                  )
                }
              >
                <i className="fi fi-rr-download"></i>
                Download
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default ImageCreation;
